import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Select } from '~/components/Form';
import { t, validCharacters } from '~/helpers';
import {
  storeOperacao,
  updateOperacao,
} from '~/store/modules/operacao/actions';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.operacao);

  const initials = create ? data : { ...find, ...data };

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        idoperacao: Yup.number()
          .transform((value, originalValue) => {
            if (
              originalValue === '' ||
              originalValue === null ||
              originalValue === undefined
            ) {
              return null;
            }
            return Number(originalValue);
          })
          .typeError(t('s.geral.validacao.informeumnumero'))
          .required(t('s.geral.validacao.campoobrigatorio'))
          .integer(t('s.geral.validacao.somentenumerointeiro'))
          .min(1, t('s.geral.validacao.valorminimo').replace('%s', '1'))
          .max(
            2147483647,
            t('s.geral.validacao.tamanhomaximo').replace('%s', '2147483647')
          ),
        descricaooperacao: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(30, t('s.geral.validacao.tamanhomaximo').replace('%s', 30))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        statusoperacao: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(1, t('s.geral.validacao.tamanhomaximo').replace('%s', 1)),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        ...initials,
        ...param,
        idcliente: client,
      };

      if (create) {
        await dispatch(storeOperacao(objSend, params));
      } else {
        await dispatch(updateOperacao(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Input
            name="idoperacao"
            label={t('s.cadastro.operacao.idoperacao')}
            variant="outlined"
            fullWidth
            disabled={!create}
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
          <Input
            name="descricaooperacao"
            label={t('s.cadastro.operacao.descricaooperacao')}
            variant="outlined"
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Select
            name="statusoperacao"
            variant="outlined"
            label={t('s.cadastro.operacao.statusoperacao')}
            options={[
              { value: 'L', label: t('s.geral.opcoes.liberado') },
              { value: 'B', label: t('s.geral.opcoes.bloqueado') },
            ]}
          />
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
